import type ApolloCache from '@wora/apollo-cache'
import type { DealQuery, Deal, SearchDealsQuery } from '../generated/types'
import { DealDocument, SearchDealsDocument, SearchedDealFragmentDoc, FullDealFragmentDoc } from '../generated/types'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import type { CacheProxy } from '.'
import { updateQueries } from './util'

export interface UpdateCachedDealOptions {
  /**
   * Drop this deal from every cached `searchDeals` result.
   *
   * This exists so callers don't have to lie about `_deleted` to hide a row. A delete from the UI is a
   * *soft* delete — the server only sets `trashed`/`deleteDate` and the `deal` resolver still returns
   * the document — but `_deleted` is CouchDB's tombstone marker, so writing it into the cache to mean
   * "hide from lists" put a value in the cache that permanently deletes the deal if it ever round-trips
   * back through `saveDeal`. (#22138)
   */
  removeFromLists?: boolean
}

/**
 * Pure transform: strip the given ids out of one cached `searchDeals`-shaped page.
 *
 * `total_rows` is decremented by however many rows were *actually* removed from this page — never
 * `removeIds.length`, and never below zero. Two things depend on that:
 *   - a given id may not appear in every cached variant, so a blind `- removeIds.length` corrupts the
 *     count of variants that never held the row; and
 *   - the deal list views run their own removal pass over the same cached variants after the mutation
 *     resolves (`removeRowsFromQuery` in `ListView`/`DealDocList`), so this has to be idempotent or the
 *     count double-decrements. Those views pass `skipRefetchQueries`, so there is no refetch afterwards
 *     to correct a bad count — being idempotent here is the only thing keeping them accurate.
 *
 * Mirrors `filterSearchQuotesByIds` in `apps/shared/hooks/quoteCacheHelpers.ts`.
 */
export function filterDealRowsByIds<T extends { deals?: Array<{ _id: string }> | null; total_rows?: number | null }>(
  rows: T,
  removeIds: string[]
): T {
  if (!rows?.deals) return rows

  const removeSet = new Set(removeIds)
  const filtered = rows.deals.filter((deal) => !removeSet.has(deal._id))
  const removed = rows.deals.length - filtered.length

  return {
    ...rows,
    deals: filtered,
    total_rows: Math.max(0, (rows.total_rows ?? 0) - removed)
  }
}

/**
 * Updates any cached deals in the apollo cache
 */
export function updateCachedDeal(proxy: CacheProxy, deal: Deal, options?: UpdateCachedDealOptions) {
  const cache = proxy as ApolloCache
  // `DealDocument` is `query deal($id: String!)`, so the variable MUST be named `id` — Apollo derives
  // the root store key from the document's declared arguments (`deal({"id":"<id>"})`). Writing `_id`
  // here instead silently landed the deal under `deal({})`, which nothing reads: watched `deal`
  // queries never picked it up, `cache-first` readers always missed, and `updateCachedDealPartially`'s
  // `readQuery({ variables: { id } })` below threw straight into its console.warn no-op. (#22098)
  cache.writeQuery({
    query: DealDocument,
    variables: { id: deal._id },
    data: {
      deal: {
        __typename: 'Deal',
        ...deal
      }
    }
  })

  // update all searchDeals queries to remove the deal. `_deleted` stays a trigger so a genuinely
  // tombstoned deal still leaves the lists; `removeFromLists` covers the soft-delete case that used
  // to have to fake `_deleted` to get here.
  if (options?.removeFromLists || deal._deleted) {
    updateQueries<SearchDealsQuery>(cache, {
      query: SearchDealsDocument,
      update: (data) => ({
        ...data,
        searchDeals: filterDealRowsByIds(data.searchDeals, [deal._id])
      })
    })
  }

  cache.writeFragment({
    fragment: SearchedDealFragmentDoc,
    id: defaultDataIdFromObject({ id: deal._id, __typename: 'Deal' }),
    data: {
      __typename: 'Deal',
      ...deal
    }
  })

  cache.writeFragment({
    fragment: FullDealFragmentDoc,
    id: defaultDataIdFromObject({ id: deal._id, __typename: 'Deal' }),
    data: {
      __typename: 'Deal',
      ...deal
    }
  })
}

export function updateCachedDealPartially(
  cache: ApolloCache,
  dealId: string,
  values: Partial<Deal>,
  options?: UpdateCachedDealOptions
) {
  try {
    const deal = cache.readQuery<DealQuery>({
      query: DealDocument,
      variables: { id: dealId }
    }).deal

    updateCachedDeal(
      cache,
      {
        ...deal,
        ...values
      },
      options
    )
  } catch (_e) {
    console.warn('Unable to find deal for id ' + dealId)
  }
}
